import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { reportClientError } from '@/lib/report-error';

interface Props {
  children: ReactNode;
  /** When any value here changes, a thrown error is cleared (e.g. on navigation),
   * so moving to another page recovers without a full reload. */
  resetKeys?: unknown[];
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Catches render errors anywhere in its subtree and shows a recoverable fallback
 * instead of letting React unmount the whole tree to a blank white screen. Pair
 * with `resetKeys={[location]}` so navigating away clears the error.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught a render error:', error, info.componentStack);
    reportClientError(error, 'react-error-boundary', {
      componentStack: info.componentStack ?? undefined,
    });
  }

  componentDidUpdate(prevProps: Props) {
    if (!this.state.hasError) return;
    const { resetKeys } = this.props;
    const prev = prevProps.resetKeys;
    if (resetKeys && prev && resetKeys.some((k, i) => k !== prev[i])) {
      this.setState({ hasError: false });
    }
  }

  handleReset = () => this.setState({ hasError: false });

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div className="flex min-h-[60dvh] flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="text-xl font-semibold">Something went wrong</h1>
        <p className="max-w-md text-sm text-muted-foreground">
          This page hit an unexpected error. Try again, or reload the page if it keeps happening.
        </p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={this.handleReset}>
            Try again
          </Button>
          <Button onClick={() => window.location.reload()}>Reload page</Button>
        </div>
      </div>
    );
  }
}
